import { getName } from "country-list";

/**
 * Returns true only for 2-character strings that are either the "ZZ" unknown
 * sentinel or not a recognised ISO 3166-1 alpha-2 code.
 * Strings longer than 2 characters are assumed to already be display names
 * and are never treated as unknown codes.
 */
export function isUnknownCountryCode(code: string): boolean {
  if (code.length !== 2) return false;

  return code.toUpperCase() === "ZZ" || getName(code) === undefined;
}

/**
 * Maps an ISO 3166-1 alpha-2 country code to its full English name.
 * - 2-char strings are looked up; "ZZ" or unrecognised codes return "Unknown".
 * - Longer strings are treated as display names already and returned as-is.
 */
export function countryCodeToName(code: string): string {
  if (code.length !== 2) return code;
  if (isUnknownCountryCode(code)) return "Unknown";

  return getName(code)!;
}

export const overWriteStartDate = (
  computedStartDate: Date,
  earliestSelectableDate?: Date,
): Date => {
  if (!earliestSelectableDate) return computedStartDate;

  return computedStartDate < earliestSelectableDate
    ? earliestSelectableDate
    : computedStartDate;
};

export const parseMinSessionDuration = (raw: unknown): number | null =>
  typeof raw === "number" && Number.isInteger(raw) && raw >= 0 ? raw : null;

export const getInitialSelectedYear = (date: Date | null): number => {
  return date ? date.getUTCFullYear() : new Date().getUTCFullYear();
};

export const getInitialSelectedMonth = (date: Date | null): number => {
  return date ? date.getUTCMonth() : new Date().getUTCMonth();
};

/**
 * Distributes 100 percentage points across `values` using the Largest Remainder
 * Method, so the rounded percentages always sum to exactly
 * `Math.round(sum of exact percentages)` — typically 100.
 *
 * This prevents the independent-Math.round over-sum problem (e.g. 103%).
 *
 * @param values - Raw metric values for the items that will show a real %.
 * @param total  - The denominator (total across ALL items, including any N/A ones).
 * @returns      - Integer percentages in the same order as `values`.
 */
export function largestRemainderPercentages(
  values: number[],
  total: number,
): number[] {
  if (total === 0 || values.length === 0) return values.map(() => 0);

  const exact = values.map((v) => (v / total) * 100);
  const target = Math.round(exact.reduce((s, v) => s + v, 0));
  const floors = exact.map(Math.floor);
  const remainder = target - floors.reduce((s, v) => s + v, 0);

  const byRemainder = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  const result = [...floors];
  for (let k = 0; k < remainder; k++) {
    result[byRemainder[k]!.i]! += 1;
  }

  return result;
}
